import { ClientPortalProvider } from "./pages/client/ClientPortalContext";
import { EmailComposerProvider } from "./contexts/EmailComposerContext";
// src/App.js
import React, { useEffect, useState } from "react";
// These legacy styles are shared by several routes. Load them once in a stable
// order so opening a lazily loaded module cannot change their precedence.
import "./components/receipts/ReceiptLibrary.css";
import "./components/InvoiceGenerator.css";
import "./components/SalesTracker.css";
import "./components/InventoryManager.css";
import "./components/AccountingManager.css";
import "./components/SavedInvoicesPanel.css";
import "./components/InvoiceOnlinePaymentPanel.css";
import RouteLoadBoundary from "./components/RouteLoadBoundary";
import { LEGAL_LINKS } from "./config/legalLinks";
import { useTranslation } from "react-i18next";
import { moduleText as ui } from "./i18n/moduleText";
import {
  BrowserRouter as Router,
  Routes,
  Link,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { useAuth } from "./contexts/AuthContext";
import { db } from "./services/firebase";
import { getModuleById } from "./config/sbsModules";
import AuthPage from "./pages/AuthPage";

const Transactions = React.lazy(() => import("./pages/Transactions"));
const Payroll = React.lazy(() => import("./pages/Payroll"));
const CigarBreakEvenPage = React.lazy(() => import("./pages/CigarBreakEvenPage"));
const BillingPage = React.lazy(() => import("./pages/BillingPage"));

import PrivateRoute from "./components/PrivateRoute";
import AdminRoute from "./components/AdminRoute";
const OperationsDashboard = React.lazy(() => import("./components/OperationsDashboard"));
const TodayInSBS = React.lazy(() => import("./components/TodayInSBS"));
const ClientOnboarding = React.lazy(() => import("./components/ClientOnboarding"));
import SBSLogoMark from "./components/SBSLogoMark";
const PricingPage = React.lazy(() => import("./components/PricingPage"));
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsOfService from "./components/TermsOfService";
const AccountDeletion = React.lazy(() => import("./components/AccountDeletion"));
const CompanyAdminPanel = React.lazy(() => import("./components/CompanyAdminPanel"));
import SysAdminRoute from "./admin/sys-admin/SysAdminRoute";
const SysAdminApp = React.lazy(() => import("./admin/sys-admin/SysAdminApp"));

const CRMDashboard = React.lazy(() => import("./pages/crm/CRMDashboard"));
const CRMContacts = React.lazy(() => import("./pages/crm/CRMContacts"));
const CRMContactDetail = React.lazy(() => import("./pages/crm/CRMContactDetail"));
const CRMClients = React.lazy(() => import("./pages/crm/CRMClients"));
const CRMDeals = React.lazy(() => import("./pages/crm/CRMDeals"));
const CRMProjects = React.lazy(() => import("./pages/crm/CRMProjects"));
const CRMProjectDetail = React.lazy(() => import("./pages/crm/CRMProjectDetail"));
const CRMTasks = React.lazy(() => import("./pages/crm/CRMTasks"));
const CRMActivity = React.lazy(() => import("./pages/crm/CRMActivity"));
const OperationsCenter = React.lazy(() => import("./components/OperationsCenter"));
const ClientDashboard = React.lazy(() => import("./pages/client/ClientDashboard"));
const ClientDocuments = React.lazy(() => import("./pages/client/ClientDocuments"));
const ClientProjects = React.lazy(() => import("./pages/client/ClientProjects"));
const ClientProjectDetail = React.lazy(() => import("./pages/client/ClientProjectDetail"));
const BusinessFinanceCenter = React.lazy(() => import("./components/BusinessFinanceCenter"));
const DistributionRoutes = React.lazy(() => import("./modules/distribution/distributionRoutes"));
import { SBSFeedbackProvider } from "./components/feedback/SBSFeedbackProvider";
import { SBSHelpProvider } from "./components/help/SBSHelpProvider";
const ManagerialEconomicsApp = React.lazy(() => import("./components/managerialEconomics/ManagerialEconomicsApp"));
const MarketingCenterApp = React.lazy(() => import("./modules/marketing/MarketingCenterApp"));
const DerickWorkspace = React.lazy(() => import("./components/derick/DerickWorkspace"));
const HRCenter = React.lazy(() => import("./modules/hr/HRCenter"));
const CigarRetailApp = React.lazy(() => import("./modules/cigar-retail/CigarRetailApp"));
const SettingsPage = React.lazy(() => import("./pages/SettingsPage"));
const CollaboratorInvitationPage = React.lazy(() => import("./pages/CollaboratorInvitationPage"));
const PublicInvoicePaymentPage = React.lazy(() => import("./pages/PublicInvoicePaymentPage"));
const WebsiteConnectorPage = React.lazy(() => import("./pages/WebsiteConnectorPage"));
import DerickSupportAssistant from "./components/support/DerickSupportAssistant";
const OpenSourceCredits = React.lazy(() => import("./pages/OpenSourceCredits"));
const CustomerQuotePage = React.lazy(() => import("./pages/CustomerQuotePage"));
const CustomerShipmentPage = React.lazy(() => import("./pages/CustomerShipmentPage"));
import {
  canAccessJimdeDistributionModule,
  isSelennePlatformAdmin,
  isLimitedDistributionAdmin,
} from "./utils/distributionAccess";
import { isGooglePlayApp } from "./utils/appChannel";
import { needsSubscriptionSelection } from "./utils/authFlow";
import { isFinanceOnlyUser, canOpenFinanceRoute } from "./utils/financeAccess";
import WorkspaceBar from "./components/workspace/WorkspaceBar";
import { WorkspaceEvidence, WorkspaceExperienceProvider, WorkspaceFrame } from "./components/workspace/WorkspaceExperience";

const SmartTmsApp = React.lazy(() => import("./modules/tms/SmartTmsApp"));

const OWNER_EMAILS = [
  "antonio@selennestrategies.com",
  "mtantonio3@gmail.com",
];
const DEMO_COMPANY_ID = "demo-company";
const DASHBOARD_REVIEWER_ROLE = "dashboard_reviewer";
const BLOCKED_ACCESS_STATUSES = new Set(["suspended", "revoked"]);
const BLOCKED_SUBSCRIPTION_STATUSES = new Set([
  "cancelled",
  "canceled",
  "suspended",
  "revoked",
]);
const BLOCKED_COMPANY_STATUSES = new Set(["cancelled", "canceled", "suspended"]);

function LoadingScreen({ message = "Preparing your workspace." }) {
  return (
    <div style={loadingPageStyle}>
      <div style={loadingCardStyle}>
        <h2 style={{ margin: 0 }}>{ui("Loading...")}</h2>
        <p style={{ color: "#6b7280" }}>{ui(message)}</p>
      </div>
    </div>
  );
}

function SignInRedirect() {
  const location = useLocation();
  const returnTo = `${location.pathname}${location.search}${location.hash}`;

  if (isGooglePlayApp() && location.pathname === "/") {
    return <Navigate to="/pricing?source=google-play" replace />;
  }

  return (
    <Navigate
      to={`/auth?returnTo=${encodeURIComponent(returnTo)}`}
      replace
    />
  );
}

function normalizeStatus(value) {
  return String(value || "").trim().toLowerCase();
}

function isOwnerEmail(currentUser, userData) {
  const email = (currentUser?.email || userData?.email || "").toLowerCase();
  return OWNER_EMAILS.includes(email);
}

function isUserSuspended(userData) {
  return BLOCKED_ACCESS_STATUSES.has(normalizeStatus(userData?.accessStatus));
}

function isSubscriptionAllowed(userData) {
  return !BLOCKED_SUBSCRIPTION_STATUSES.has(
    normalizeStatus(userData?.subscriptionStatus)
  );
}

function isCompanyStatusAllowed(companyData) {
  return (
    !BLOCKED_COMPANY_STATUSES.has(normalizeStatus(companyData?.companyStatus)) &&
    !BLOCKED_COMPANY_STATUSES.has(normalizeStatus(companyData?.subscriptionStatus))
  );
}

function isValidDashboardReviewer(userData) {
  return (
    userData?.role === DASHBOARD_REVIEWER_ROLE &&
    userData?.demoMode === true &&
    userData?.companyId === DEMO_COMPANY_ID
  );
}

function hasModuleAccess(userData, moduleName) {
  return (
    userData?.modules?.[moduleName] === true ||
    userData?.allowedModules?.includes(moduleName) ||
    userData?.allowedModules?.includes("all")
  );
}

function isEnabledCompanyModule(moduleId, companyModules, userData) {
  const moduleInfo = getModuleById(moduleId);

  if (!moduleInfo) return false;

  const companyConfig = companyModules?.[moduleId];

  if (typeof companyConfig === "boolean") {
    return companyConfig;
  }

  if (typeof companyConfig?.enabled === "boolean") {
    return companyConfig.enabled;
  }

  if (userData?.modules?.[moduleId] === true) {
    return true;
  }

  if (
    userData?.allowedModules?.includes(moduleId) ||
    userData?.allowedModules?.includes("all")
  ) {
    return true;
  }

  return Boolean(moduleInfo.defaultEnabled);
}

function isUserApproved(currentUser, userData) {
  if (isUserSuspended(userData) || !isSubscriptionAllowed(userData)) {
    return false;
  }

  if (userData?.collaboratorSeatActive === false) {
    return false;
  }

  if (userData?.role === DASHBOARD_REVIEWER_ROLE) {
    return isValidDashboardReviewer(userData);
  }

  return (
    isOwnerEmail(currentUser, userData) ||
    userData?.accessApproved === true ||
    userData?.accessStatus === "approved" ||
    userData?.role === "owner" ||
    userData?.role === "admin"
  );
}

function isApprovedAccount(currentUser, userData) {
  return isUserApproved(currentUser, userData);
}
function isDistributionCustomer(userData) {
  return (
    userData?.role === "distributionCustomer" ||
    userData?.distributionRole === "customer" ||
    userData?.allowedModules?.includes("distributionCustomer")
  );
}
function AppAccessRoute({ children }) {
  const { currentUser, userData, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!currentUser) {
    return <SignInRedirect />;
  }

  if (!isApprovedAccount(currentUser, userData)) {
    return <PendingApprovalPage />;
  }

  return children;
}

function PendingApprovalPage() {
  const { signOutUser, userData, currentUser, refreshUserData } = useAuth();
  const showPlanRoute = needsSubscriptionSelection(userData);

  return (
    <div style={pendingPageStyle}>
      <div style={pendingCardStyle}>
        <SBSLogoMark alt="" style={pendingBadgeStyle} />

        <h1 style={pendingTitleStyle}>Account Pending Approval</h1>

        <p style={pendingTextStyle}>
          Your account has been created, but dashboard access has not been
          approved yet.
        </p>

        <p style={pendingSmallTextStyle}>
          Signed in as: {userData?.email || currentUser?.email || "Unknown user"}
        </p>

        <p style={pendingSmallTextStyle}>
          Please contact Selenne Strategies to activate your account.
        </p>

        <div style={pendingButtonRowStyle}>
          {showPlanRoute && (
            <button
              type="button"
              onClick={() => {
                window.location.href = "/pricing";
              }}
              style={pendingButtonStyle}
            >
              View Subscription Plans
            </button>
          )}

          <button
            type="button"
            onClick={refreshUserData}
            style={{
              ...pendingButtonStyle,
              background: "#ffffff",
              color: "#181717",
            }}
          >
            Refresh Access
          </button>

          <button type="button" onClick={signOutUser} style={pendingButtonStyle}>
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}

function ModuleUnavailablePage({ moduleId }) {
  const moduleInfo = getModuleById(moduleId);
  const moduleLabel = moduleInfo?.label || "This module";
  const moduleDescription =
    moduleInfo?.description ||
    "This product area is not currently enabled for this company workspace.";

  return (
    <div style={pendingPageStyle}>
      <div style={moduleUnavailableCardStyle}>
        <SBSLogoMark alt="" style={pendingBadgeStyle} />

        <div style={moduleUnavailablePillStyle}>Module Access</div>

        <h1 style={pendingTitleStyle}>Module Not Enabled</h1>

        <p style={moduleUnavailableModuleNameStyle}>{moduleLabel}</p>

        <p style={pendingTextStyle}>{moduleDescription}</p>

        <div style={moduleUnavailableNoticeStyle}>
          <strong>Access blocked</strong>
          <span>
            This company workspace does not currently include access to this
            module. The route is protected even if someone enters the URL
            manually.
          </span>
        </div>

        <p style={pendingSmallTextStyle}>
          Contact your company admin or Selenne Strategies if this module should
          be added to your workspace.
        </p>

        <div style={pendingButtonRowStyle}>
          <button
            type="button"
            onClick={() => {
              window.location.href = "/";
            }}
            style={pendingButtonStyle}
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}

function ModuleAccessRoute({ moduleId, children }) {
  const { currentUser, userData, loading } = useAuth();
  const hasPlatformDistributionOverride =
    moduleId === "distribution" &&
    isSelennePlatformAdmin(currentUser, userData);

  const [checkingModule, setCheckingModule] = useState(true);
  const [moduleAllowed, setModuleAllowed] = useState(false);
  const [workspaceEvidence, setWorkspaceEvidence] = useState(null);
  const workspaceEvidenceKey = JSON.stringify([currentUser?.uid, userData?.companyId, moduleId, userData?.modules, userData?.allowedModules, userData?.accessStatus, userData?.subscriptionStatus, userData?.role]);

  useEffect(() => {
    const checkModuleAccess = async () => {
      if (loading) return;

      if (!currentUser) {
        setModuleAllowed(false);
        setCheckingModule(false);
        return;
      }

      if (
        !isApprovedAccount(currentUser, userData) &&
        !hasPlatformDistributionOverride
      ) {
        setModuleAllowed(false);
        setCheckingModule(false);
        return;
      }

      if (hasPlatformDistributionOverride) {
        setWorkspaceEvidence({ key: workspaceEvidenceKey, modules: null });
        setModuleAllowed(true);
        setCheckingModule(false);
        return;
      }

      try {
        let companyModules = null;
        const companyId = userData?.companyId || null;

        if (companyId) {
          const companyRef = doc(db, "companies", companyId);
          const companySnap = await getDoc(companyRef);
          const companyData = companySnap.exists() ? companySnap.data() : {};

          if (!isCompanyStatusAllowed(companyData)) {
            setModuleAllowed(false);
            return;
          }

          companyModules = companyData.modules || null;
        }

        setModuleAllowed(
          isEnabledCompanyModule(moduleId, companyModules, userData)
        );
        setWorkspaceEvidence({ key: workspaceEvidenceKey, modules: companyModules });
      } catch (err) {
        console.error("Module route access check failed:", err);
        setModuleAllowed(false);
      } finally {
        setCheckingModule(false);
      }
    };

    checkModuleAccess();
  }, [
    currentUser,
    userData,
    loading,
    moduleId,
    hasPlatformDistributionOverride,
    workspaceEvidenceKey,
  ]);

  if (loading || checkingModule) {
    return <LoadingScreen message="Checking module access." />;
  }

  if (!currentUser) {
    return <SignInRedirect />;
  }

  if (
    !isApprovedAccount(currentUser, userData) &&
    !hasPlatformDistributionOverride
  ) {
    return <PendingApprovalPage />;
  }

  if (!moduleAllowed) {
    return <ModuleUnavailablePage moduleId={moduleId} />;
  }

  return <WorkspaceEvidence ready={workspaceEvidence?.key === workspaceEvidenceKey} module={moduleId} modules={workspaceEvidence?.modules}>{children}</WorkspaceEvidence>;
}

function ProtectedPage({ children }) {
  const { currentUser, userData, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!currentUser) {
    return <SignInRedirect />;
  }

  if (
    isDistributionCustomer(userData) &&
    canAccessJimdeDistributionModule(currentUser, userData)
  ) {
    return <Navigate to="/distribution/customer/orders" replace />;
  }

  if (
    isLimitedDistributionAdmin(userData) &&
    canAccessJimdeDistributionModule(currentUser, userData)
  ) {
    return <Navigate to="/distribution" replace />;
  }

  if (userData?.role === "client") {
    return <Navigate to="/client" replace />;
  }

  return (
    <PrivateRoute>
      <AppAccessRoute><WorkspaceEvidence>{children}</WorkspaceEvidence></AppAccessRoute>
    </PrivateRoute>
  );
}

function ClientPage({ children }) {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return <LoadingScreen message="Preparing your client portal." />;
  }

  if (!currentUser) {
    return <SignInRedirect />;
  }

  return <ClientPortalProvider>{children}</ClientPortalProvider>;
}
function DistributionAccessPage({ children }) {
  const { currentUser, userData, loading } = useAuth();

  if (loading) {
    return <LoadingScreen message="Preparing your distribution workspace." />;
  }

  if (!currentUser) {
    return <SignInRedirect />;
  }

  const isPlatformAdmin = isSelennePlatformAdmin(currentUser, userData);

  if (!canAccessJimdeDistributionModule(currentUser, userData)) {
    return <Navigate to="/" replace />;
  }

  if (!isApprovedAccount(currentUser, userData) && !isPlatformAdmin) {
    return <PendingApprovalPage />;
  }

  const hasDistributionAccess =
    isPlatformAdmin ||
    isOwnerEmail(currentUser, userData) ||
    userData?.role === "owner" ||
    userData?.role === "admin" ||
    userData?.role === "distributionAdmin" ||
    userData?.distributionRole === "admin" ||
    userData?.allowedModules?.includes("distribution") ||
    (isValidDashboardReviewer(userData) &&
      hasModuleAccess(userData, "distribution")) ||
    isDistributionCustomer(userData);

  if (!hasDistributionAccess) {
    return <Navigate to="/" replace />;
  }

  return children;
}
function AppWrapper() {
  useTranslation();
  const location = useLocation();
  const { userData } = useAuth();
  const googlePlayApp = isGooglePlayApp();

  if (isFinanceOnlyUser(userData) && !canOpenFinanceRoute(location.pathname) && !["/open-source-credits", "/quote", "/shipment"].includes(location.pathname)) {
    return <Navigate to="/finance" replace />;
  }

  const hideGlobalFooter =
    location.pathname === "/auth" ||
    location.pathname === "/quote" ||
    location.pathname === "/shipment" ||
    location.pathname === "/" ||
    location.pathname.startsWith("/operations") ||
    location.pathname.startsWith("/billing") ||
	location.pathname.startsWith("/finance") ||
    location.pathname.startsWith("/crm") ||
    location.pathname.startsWith("/client") ||
    location.pathname.startsWith("/cigar-retail") ||
    location.pathname.startsWith("/distribution") ||
	location.pathname.startsWith("/managerial-economics") ||
    location.pathname.startsWith("/marketing-center") ||
    location.pathname.startsWith("/derick") ||
    location.pathname.startsWith("/hr") ||
    location.pathname.startsWith("/admin") ||
    location.pathname.startsWith("/dashboard") ||
    location.pathname.startsWith("/transactions") ||
    location.pathname.startsWith("/rapid-record") ||
    location.pathname.startsWith("/payroll");

  return (
    <WorkspaceFrame>
      <WorkspaceBar />
      <RouteLoadBoundary key={location.pathname}>
      <React.Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/join/:token" element={<CollaboratorInvitationPage />} />
        <Route
          path="/pay/invoice/:companyId/:invoiceId"
          element={<PublicInvoicePaymentPage />}
        />
        <Route path="/onboarding" element={<ClientOnboarding />} />
        <Route
          path="/pricing"
          element={<PricingPage />}
        />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/open-source-credits" element={<OpenSourceCredits />} />
        <Route path="/quote" element={<React.Suspense fallback={<div role="status">SBS Smart TMS…</div>}><CustomerQuotePage /></React.Suspense>} />
        <Route path="/shipment" element={<React.Suspense fallback={<div role="status">SBS Smart TMS…</div>}><CustomerShipmentPage /></React.Suspense>} />
        <Route path="/account-deletion" element={<AccountDeletion />} />
        <Route path="/tools/cigar-break-even" element={<CigarBreakEvenPage />} />

        {/* Client Portal Routes */}
        <Route
          path="/client"
          element={
            <ClientPage>
              <ClientDashboard />
            </ClientPage>
          }
        />

        <Route
          path="/client/projects"
          element={
            <ClientPage>
              <ClientProjects />
            </ClientPage>
          }
        />

        <Route
          path="/client/projects/:projectId"
          element={
            <ClientPage>
              <ClientProjectDetail />
            </ClientPage>
          }
        />

        {/* Main SBS Dashboard */}
        <Route path="/client/documents" element={<ClientPage><ClientDocuments /></ClientPage>} />

        <Route
          path="/"
          element={
            <ProtectedPage>
              <TodayInSBS />
            </ProtectedPage>
          }
        />

        <Route
		  path="/operations"
		  element={
			<ProtectedPage>
			  <OperationsCenter />
			</ProtectedPage>
		  }
		/>
		<Route
		  path="/managerial-economics/*"
		  element={
			<ProtectedPage>
			  <ModuleAccessRoute moduleId="operations">
				<ManagerialEconomicsApp />
			  </ModuleAccessRoute>
			</ProtectedPage>
		  }
		/>
		<Route
		  path="/derick"
		  element={<ProtectedPage><DerickWorkspace /></ProtectedPage>}
		/>
		<Route
		  path="/marketing-center"
		  element={
			<ProtectedPage>
			  <ModuleAccessRoute moduleId="operations">
				<MarketingCenterApp />
			  </ModuleAccessRoute>
			</ProtectedPage>
		  }
		/>
		<Route
		  path="/hr/*"
		  element={
			<ProtectedPage>
			  <ModuleAccessRoute moduleId="hr">
				<HRCenter />
			  </ModuleAccessRoute>
			</ProtectedPage>
		  }
		/>

        <Route
          path="/tms/*"
          element={<ProtectedPage><React.Suspense fallback={<div role="status">SBS Smart TMS…</div>}><SmartTmsApp /></React.Suspense></ProtectedPage>}
        />
        <Route
          path="/cigar-retail/*"
          element={
            <ProtectedPage>
              <ModuleAccessRoute moduleId="cigarRetail">
                <CigarRetailApp />
              </ModuleAccessRoute>
            </ProtectedPage>
          }
        />
		
        <Route
          path="/billing"
          element={
            googlePlayApp ? (
              <Navigate to="/" replace />
            ) : (
              <ProtectedPage>
                <BillingPage />
              </ProtectedPage>
            )
          }
        />
        {/* Legacy / Supporting Pages */}
        <Route
		  path="/dashboard"
		  element={
			<ProtectedPage>
			  <OperationsDashboard />
			</ProtectedPage>
		  }
		/>

        <Route
		  path="/finance"
		  element={
			<ProtectedPage>
			  <ModuleAccessRoute moduleId="finance">
				<BusinessFinanceCenter />
			  </ModuleAccessRoute>
			</ProtectedPage>
		  }
		/>

		<Route
		  path="/transactions"
		  element={
			<ProtectedPage>
			  <ModuleAccessRoute moduleId="finance">
				<Transactions />
			  </ModuleAccessRoute>
			</ProtectedPage>
		  }
		/>
		<Route
		  path="/rapid-record"
		  element={
			<ProtectedPage>
			  <ModuleAccessRoute moduleId="finance">
				<Transactions />
			  </ModuleAccessRoute>
			</ProtectedPage>
		  }
		/>
		<Route
		  path="/rapid-record/lite"
		  element={
			<ProtectedPage>
			  <ModuleAccessRoute moduleId="finance">
				<Transactions liteMode />
			  </ModuleAccessRoute>
			</ProtectedPage>
		  }
		/>

		<Route
		  path="/payroll"
		  element={
			<ProtectedPage>
			  <ModuleAccessRoute moduleId="finance">
				<Payroll />
			  </ModuleAccessRoute>
			</ProtectedPage>
		  }
		/>
        <Route
          path="/admin/sys-admin"
          element={
            <SysAdminRoute>
              <SysAdminApp />
            </SysAdminRoute>
          }
        />


        <Route
          path="/admin"
          element={
            <ProtectedPage>
              <AdminRoute>
                <CompanyAdminPanel />
              </AdminRoute>
            </ProtectedPage>
          }
        />

        <Route
          path="/website-connector"
          element={
            <ProtectedPage>
              <AdminRoute>
                <WebsiteConnectorPage />
              </AdminRoute>
            </ProtectedPage>
          }
        />
		
		<Route
		  path="/settings"
		  element={
			<ProtectedPage>
			  <SettingsPage />
			</ProtectedPage>
		  }
		/>
		
        {/* Distribution Module Routes */}
        <Route
		  path="/distribution/*"
		  element={
			<DistributionAccessPage>
			  <ModuleAccessRoute moduleId="distribution">
				<DistributionRoutes />
			  </ModuleAccessRoute>
			</DistributionAccessPage>
		  }
		/>

        {/* CRM Routes */}
        <Route
          path="/crm"
          element={
            <ProtectedPage>
			  <ModuleAccessRoute moduleId="operations">
                <CRMDashboard />
			  </ModuleAccessRoute>
            </ProtectedPage>
          }
        />

        <Route
          path="/crm/contacts"
          element={
            <ProtectedPage>
			  <ModuleAccessRoute moduleId="operations">
                <CRMContacts />
			  </ModuleAccessRoute>
            </ProtectedPage>
          }
        />

        <Route
          path="/crm/contacts/:contactId"
          element={
            <ProtectedPage>
			  <ModuleAccessRoute moduleId="operations">
                <CRMContactDetail />
			  </ModuleAccessRoute>
            </ProtectedPage>
          }
        />

        <Route
          path="/crm/clients"
          element={
            <ProtectedPage>
			  <ModuleAccessRoute moduleId="operations">
                <CRMClients />
			  </ModuleAccessRoute>
            </ProtectedPage>
          }
        />

        <Route
          path="/crm/deals"
          element={
            <ProtectedPage>
			  <ModuleAccessRoute moduleId="operations">
                <CRMDeals />
			  </ModuleAccessRoute> 
            </ProtectedPage>
          }
        />

        <Route
          path="/crm/projects"
          element={
            <ProtectedPage>
			  <ModuleAccessRoute moduleId="operations">
                <CRMProjects />
			  </ModuleAccessRoute>  
            </ProtectedPage>
          }
        />

        <Route
          path="/crm/projects/:projectId"
          element={
            <ProtectedPage>
			  <ModuleAccessRoute moduleId="operations">
                <CRMProjectDetail />
			  </ModuleAccessRoute>
            </ProtectedPage>
          }
        />

        <Route
          path="/crm/tasks"
          element={
            <ProtectedPage>
			  <ModuleAccessRoute moduleId="operations">
                <CRMTasks />
			  </ModuleAccessRoute>
            </ProtectedPage>
          }
        />

        <Route
          path="/crm/activity"
          element={
            <ProtectedPage>
			  <ModuleAccessRoute moduleId="operations">
                <CRMActivity />
			  </ModuleAccessRoute>
            </ProtectedPage>
          }
        />
		
		
        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </React.Suspense>
      </RouteLoadBoundary>

      {!isFinanceOnlyUser(userData) && !["/quote", "/shipment"].includes(location.pathname) && <DerickSupportAssistant />}

      {!hideGlobalFooter && (
        <footer className="sbs-footer">
          &copy; {new Date().getFullYear()} {ui("Selenne Business Suite. All rights reserved.")}
          &nbsp;|&nbsp;
          <a
            href={LEGAL_LINKS.privacy}
            className="footer-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            {ui("Privacy Policy")}
          </a>
          &nbsp;|&nbsp;
          <a
            href={LEGAL_LINKS.terms}
            className="footer-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            {ui("Terms of Service")}
          </a>
          &nbsp;|&nbsp;<Link to="/open-source-credits" className="footer-link">{ui('Open-source and Map Credits')}</Link>
        </footer>
      )}
    </WorkspaceFrame>
  );
}

export default function App() {
  return (
    <SBSFeedbackProvider>
      <EmailComposerProvider>
        <SBSHelpProvider>
          <Router>
            <WorkspaceExperienceProvider><AppWrapper /></WorkspaceExperienceProvider>
          </Router>
        </SBSHelpProvider>
      </EmailComposerProvider>
    </SBSFeedbackProvider>
  );
}

const loadingPageStyle = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#f5f6f8",
};

const loadingCardStyle = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: "14px",
  padding: "28px",
  minWidth: "280px",
  textAlign: "center",
  boxShadow: "0 12px 30px rgba(0,0,0,0.08)",
};

const pendingPageStyle = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background:
    "radial-gradient(circle at top right, rgba(212, 175, 55, 0.16), transparent 28%), #f7f4ec",
  padding: "20px",
};

const pendingCardStyle = {
  width: "min(460px, 100%)",
  background: "#ffffff",
  border: "1px solid rgba(212, 175, 55, 0.45)",
  borderRadius: "18px",
  padding: "32px",
  textAlign: "center",
  boxShadow: "0 18px 50px rgba(24, 23, 23, 0.12)",
};

const pendingBadgeStyle = {
  width: "64px",
  height: "64px",
  margin: "0 auto 16px",
  borderRadius: "14px",
  objectFit: "contain",
  display: "block",
};

const pendingTitleStyle = {
  margin: "0 0 10px",
  color: "#181717",
  fontSize: "24px",
  fontWeight: "900",
};

const pendingTextStyle = {
  color: "#374151",
  fontSize: "15px",
  lineHeight: 1.6,
};

const pendingSmallTextStyle = {
  color: "#6b7280",
  fontSize: "13px",
  lineHeight: 1.5,
};

const pendingButtonRowStyle = {
  display: "flex",
  justifyContent: "center",
  gap: "10px",
  flexWrap: "wrap",
};

const pendingButtonStyle = {
  marginTop: "16px",
  background: "#181717",
  color: "#fffbe8",
  border: "1px solid #d4af37",
  borderRadius: "10px",
  padding: "10px 18px",
  fontSize: "14px",
  fontWeight: "800",
  cursor: "pointer",
};
const moduleUnavailableCardStyle = {
  ...pendingCardStyle,
  maxWidth: "520px",
};

const moduleUnavailablePillStyle = {
  display: "inline-block",
  background: "#fffbe8",
  color: "#800020",
  border: "1px solid #d4af37",
  borderRadius: "999px",
  padding: "6px 12px",
  fontSize: "12px",
  fontWeight: "900",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  marginBottom: "14px",
};

const moduleUnavailableModuleNameStyle = {
  color: "#800020",
  fontSize: "18px",
  fontWeight: "900",
  margin: "8px 0 10px",
};

const moduleUnavailableNoticeStyle = {
  background: "#fff7ed",
  border: "1px solid #fed7aa",
  borderRadius: "14px",
  padding: "14px",
  margin: "18px 0",
  color: "#9a3412",
  display: "grid",
  gap: "6px",
  fontSize: "14px",
  lineHeight: 1.5,
};
